/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, Suspense, useCallback, useEffect, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap, jsx } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { SainAction, useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { AISources } from '../../common/ui/ai-sources';
import { AIAnswerBody } from '../ai-answer-body/ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerContentWarningAndBrandSection, SearchPageSmartAnswerFooter } from '../ai-answer-footer/ai-answer-footer';
import { AIAnswerLoadingStream } from '../ai-answer-loading/ai-answer-loading-stream';
import GeneratingAnimation from '../ai-answer-loading/generating-animation';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
	aiAnswerExpandShown,
	getSmartLinksAnalyticsContextData,
	onAIAnswerLoadingDialogShown,
	onAIAnswerSourceClick,
	SmartLinksAnalyticsContextLocation,
} from '../analytics';
import { NLPSearchResultFormat, type NLPSearchResultSource } from '../types';

import { Collapse } from './collapse';
import { messages } from './messages';
import { AIAnswerSearchBorder, GeneratingContainer, GeneratingText } from './styled';

const styles = cssMap({
	spacer: {
		height: '14px',
		paddingTop: token('space.250'),
	},
	errorContainer: {
		paddingTop: token('space.100'),
	},
	sourcesContainer: {
		paddingTop: '0',
		paddingRight: token('space.300'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.300'),
	},
});

const MAX_SOURCES = 4;

const SmartAnswerLoadingDialog = () => {
	const { commonAttributes, partialResponse, queryReasoning } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { serveExperiment: serveSAINReasoningExperiment } = getFrontendExperiment({
		abTestId: 'kd_sain_reasoning_exp',
	});

	// no need for the Collapse component when showing the ... generating animation
	if (!partialResponse || partialResponse?.format !== NLPSearchResultFormat.MARKDOWN) {
		return (
			<AIAnswerSearchBorder isLoading isStreaming={false}>
				<GeneratingContainer>
					<GeneratingAnimation />
					<GeneratingText>
						{serveSAINReasoningExperiment && queryReasoning ? (
							queryReasoning
						) : (
							<FormattedMessage {...messages.ai_answer_generating_rovo} />
						)}
					</GeneratingText>
				</GeneratingContainer>
				<AIAnswerContentWarningAndBrandSection />
			</AIAnswerSearchBorder>
		);
	}
	return (
		<AnalyticsContext
			data={getSmartLinksAnalyticsContextData({
				location: SmartLinksAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
				commonAttributes,
			})}
		>
			<SmartCardProvider>
				<AIAnswerSearchBorder isLoading isStreaming>
					<Suspense fallback={<Fragment />}>
						<Collapse collapsed isLoading isStreaming>
							<AIAnswerLoadingStream markdown={partialResponse.partialNlpResult} />
						</Collapse>
						<AIAnswerContentWarningAndBrandSection />
					</Suspense>
				</AIAnswerSearchBorder>
			</SmartCardProvider>
		</AnalyticsContext>
	);
};

type AnswerCardProps = {
	onFMPReached?: () => void;
	onAnswerExpandedChange?: (expanded: boolean) => void;
};

const AnswerCard = ({ onFMPReached, onAnswerExpandedChange }: AnswerCardProps) => {
	const { hasError, commonAttributes, callbacks, initiallyExpanded, loading } =
		useAIAnswerContext();
	const { answerFormat, query } = commonAttributes;
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [collapsed, setCollapsed] = useState(true);

	// reset on each new query
	useEffect(() => {
		setCollapsed(!initiallyExpanded);
		// Notify parent context of initial state
		onAnswerExpandedChange?.(!initiallyExpanded);
	}, [query, initiallyExpanded, onAnswerExpandedChange]);

	// Call onFMPReached when answer is loaded
	useEffect(() => {
		if (!loading && !hasError) {
			onFMPReached?.();
		}
	}, [loading, hasError, onFMPReached]);

	const toggleCollapsedState = () => {
		if (collapsed) {
			fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
		}
		setCollapsed(!collapsed);
		// Notify parent context of state change
		onAnswerExpandedChange?.(!collapsed);
	};

	const analyticsCallbacks = {
		onExpandedByDefault: () => fireAnalyticsEvent(aiAnswerExpandShown(commonAttributes)),
		onExpandClicked: () => fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes)),
		onCollapseClicked: () => fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes)),
	};

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				sourceProduct: source.product || '',
				commonAttributes,
			});
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks],
	);

	let answerContent = (
		<Collapse
			collapsed={collapsed}
			toggleCollapsedState={toggleCollapsedState}
			expandWhenClickedAnywhere
			isLoading={false}
			callbacks={analyticsCallbacks}
		>
			<AIAnswerBody />
		</Collapse>
	);

	if (hasError) {
		answerContent = (
			<Box xcss={styles.errorContainer}>
				<AIAnswerBody />
			</Box>
		);
	} else if (answerFormat === NLPSearchResultFormat.JSON) {
		answerContent = (
			<Fragment>
				<Box xcss={styles.spacer}></Box>
				<AIAnswerBody />
			</Fragment>
		);
	}

	return (
		<AIAnswerSearchBorder isLoading={false} isStreaming={false}>
			{answerContent}
			{!hasError && (
				<Box xcss={styles.sourcesContainer} testId="ai-answer-sources">
					<AISources
						sources={commonAttributes.sources}
						searchSessionId={commonAttributes.searchSessionId}
						onClickHandler={sourceClickCallback}
						allowFreeflowLayout={true}
						limit={MAX_SOURCES}
					/>
				</Box>
			)}
			<SearchPageSmartAnswerFooter />
		</AIAnswerSearchBorder>
	);
};

// TODO remove this file when cleaning up `kd_sain_enable_expand_when_streaming`
/**
 * @deprecated This component is the old SmartAnswerCard that didn't support expansion while streaming
 * use SmartAnswerCard instead
 */
export const SmartAnswerCardWithoutStreamingExpand: ({ onFMPReached, onAnswerExpandedChange, }: AnswerCardProps) => JSX.Element = ({
	onFMPReached,
	onAnswerExpandedChange,
}: AnswerCardProps) => {
	const { loading } = useAIAnswerContext();
	return (
		<Fragment>
			{loading ? (
				<SmartAnswerLoadingDialog />
			) : (
				<AnswerCard onFMPReached={onFMPReached} onAnswerExpandedChange={onAnswerExpandedChange} />
			)}
		</Fragment>
	);
};
